"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CheckIcon, ChevronDown, Globe } from "lucide-react";
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";

interface LanguageOption {
  code: string;
  name: string;
  flag: string;
}

interface LanguageDropdownProps {
  onChange?: (languageCode: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  slim?: boolean;
}

const languages: LanguageOption[] = [
  { code: "de", name: "Deutsch", flag: "de" },
  { code: "en", name: "English", flag: "us" },
  { code: "es", name: "Español", flag: "es" },
  { code: "fr", name: "Français", flag: "fr" },
  { code: "it", name: "Italiano", flag: "it" },
  { code: "ja", name: "日本語", flag: "jp" },
  { code: "pt", name: "Português", flag: "br" },
];

const LanguageDropdownComponent = (
  {
    onChange,
    defaultValue,
    disabled = false,
    placeholder,
    slim = false,
    ...props
  }: LanguageDropdownProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) => {
  const { t, i18n } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState<LanguageOption | undefined>(
    undefined
  );

  const normalizeLangCode = useCallback((code?: string) => {
    if (!code) return "";
    return String(code).toLowerCase().split("-")[0];
  }, []);

  const availableLanguages = useMemo(() => {
    const resources = Object.keys(i18n.options.resources ?? {});
    return languages.filter((lang) => resources.includes(lang.code));
  }, [i18n.options.resources]);

  useEffect(() => {
    const candidates = [
      defaultValue,
      i18n.resolvedLanguage,
      i18n.language,
      typeof navigator !== "undefined" ? navigator.language : undefined,
    ].map(normalizeLangCode);

    const initialCode =
      candidates.find((code) => availableLanguages.some((l) => l.code === code)) ??
      availableLanguages[0]?.code;

    setSelectedLang(availableLanguages.find((l) => l.code === initialCode));
  }, [
    defaultValue,
    availableLanguages,
    i18n.language,
    i18n.resolvedLanguage,
    normalizeLangCode,
  ]);

  const handleSelect = useCallback(
    (lang: LanguageOption) => {
      setSelectedLang(lang);
      onChange?.(lang.code);
      setOpen(false);
    },
    [onChange]
  );

  const triggerClasses = cn(
    "flex items-center justify-between gap-1.5 rounded-full border border-colorBorder/30 bg-elevated/90 px-2.5 py-1.5 text-xs font-medium shadow-sm ring-offset-background focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 hover:bg-elevated transition-colors",
    slim && "max-w-[88px]"
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <PopoverTrigger
            ref={ref}
            className={triggerClasses}
            disabled={disabled}
            {...props}
          >
            {selectedLang ? (
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 shrink-0 overflow-hidden rounded-full">
                  <CircleFlag countryCode={selectedLang.flag} height={16} />
                </div>
                <span className="hidden sm:inline text-colorText">
                  {slim ? selectedLang.code.toUpperCase() : selectedLang.name}
                </span>
              </div>
            ) : (
              <Globe size={16} className="text-colorText" />
            )}
            <ChevronDown size={14} className="text-colorText/60" />
          </PopoverTrigger>
        </TooltipTrigger>
        {open && (
          <PopoverContent
            collisionPadding={10}
            side="bottom"
            forceMount
            className="max-w-[200px] p-0"
          >
            <Command className="w-full max-h-[200px] sm:max-h-[270px]">
              <CommandList>
                <div className="sticky top-0 z-10 bg-popover">
                  <CommandInput placeholder={t("language_search_placeholder")} />
                </div>
                <CommandEmpty>{t("language_search_placeholder")}</CommandEmpty>
                <CommandGroup>
                  {availableLanguages.map((lang) => (
                    <CommandItem
                      key={lang.code}
                      className="flex items-center w-full gap-2"
                      onSelect={() => handleSelect(lang)}
                    >
                      <div className="flex flex-grow w-0 space-x-2 overflow-hidden">
                        <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                          <CircleFlag countryCode={lang.flag} height={20} />
                        </div>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {lang.name}
                        </span>
                      </div>
                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4 shrink-0",
                          lang.code === selectedLang?.code
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
      <TooltipContent side="bottom" className="bg-elevated border-colorBorder">
        <p className="text-xs">{t('language_tooltip')}</p>
      </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

LanguageDropdownComponent.displayName = "LanguageDropdownComponent";

export const LanguageDropdown = forwardRef(LanguageDropdownComponent);